import { Fragment, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Radio,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  EXTREME_COMMENT_MIN_LEN,
  QUALITY_EVAL_RULES,
  commentTextLen,
  isExtremeScore,
} from '@talentgrid/shared';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { EvalTaskStickyHeader } from '../components/EvalTaskStickyHeader';
import { useEvalRoleLabels } from '../contexts/EvalRoleLabelsContext';
import { LEVEL_COLOR } from '../utils/scoreColor';
import { taskStatusColor, taskStatusLabel } from '../utils/taskStatusDisplay';

interface Anchor { range: string; level: '待发展' | '胜任' | '优秀' | '卓越'; desc: string; }
interface DimItem { code: string; name: string; desc: string; allowDontKnow?: boolean; anchors?: Anchor[]; }

function levelOfScore(score: number): Anchor['level'] | null {
  if (score <= 1) return '待发展';
  if (score <= 3) return '胜任';
  if (score <= 5) return '优秀';
  if (score <= 7) return '卓越';
  return null;
}

function findAnchor(anchors: Anchor[] | undefined, score: number): Anchor | undefined {
  if (!anchors) return undefined;
  const lv = levelOfScore(score);
  return anchors.find((a) => a.level === lv);
}

/** 按区间字符串（如 0-1、2～3）与子项分值（1～7）匹配对应锚点。 */
function anchorForDiscreteScore(anchors: Anchor[] | undefined, score: number): Anchor | undefined {
  if (!anchors?.length || score <= 0) return undefined;
  for (const a of anchors) {
    const m = String(a.range ?? '')
      .trim()
      .match(/^(\d+)\s*[-～]\s*(\d+)$/);
    if (!m) continue;
    const low = Number(m[1]);
    const high = Number(m[2]);
    if (score >= low && score <= high) return a;
  }
  return findAnchor(anchors, score);
}

function extremeCommentPlaceholder(score: number, dimName: string): string {
  if (score === 0) return `请说明为何无法评价「${dimName}」（至少 ${EXTREME_COMMENT_MIN_LEN} 字）`;
  if (score <= 1)
    return `请举 1 个具体事例，说明 ta 在「${dimName}」上待改进之处（至少 ${EXTREME_COMMENT_MIN_LEN} 字）`;
  return `请举 1 个具体事例，说明 ta 在「${dimName}」上的突出表现（至少 ${EXTREME_COMMENT_MIN_LEN} 字）`;
}

/** 表单内部用 { CODE: score } 方便 antd 嵌套字段；出表单时统一转成后端 DTO 要的 [{ dimCode, score, comment? }] */
function scoresObjectToArray(
  scoresObj: Record<string, number> | undefined,
  commentsObj?: Record<string, string | undefined>,
): { dimCode: string; score: number; comment?: string }[] {
  if (!scoresObj) return [];
  return Object.entries(scoresObj)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([dimCode, score]) => {
      const comment = commentsObj?.[dimCode]?.trim();
      return { dimCode, score: Number(score), ...(comment ? { comment } : {}) };
    });
}

/** 每维均已作答（1–7；允许「不了解」时为 0）方可提交 */
function allQualityDimsAnswered(dims: DimItem[], scores: Record<string, number | undefined>): boolean {
  return dims.every((d) => {
    const v = scores[d.code];
    if (v === undefined || v === null || Number.isNaN(Number(v))) return false;
    const n = Number(v);
    if (n === 0) return !!d.allowDontKnow;
    return n >= 1 && n <= 7;
  });
}

/** 与 GET /my/eval-tasks/:id 对齐：已提交读 scores[]，草稿读 draftJson */
function initialScoresFromTask(t: any): Record<string, number> {
  if (t.status === 'SUBMITTED' && Array.isArray(t.scores) && t.scores.length > 0) {
    const o: Record<string, number> = {};
    for (const s of t.scores) o[s.dimCode] = s.score;
    return o;
  }
  const raw = t.draftJson;
  if (raw && typeof raw === 'object') {
    if (Array.isArray(raw)) {
      const o: Record<string, number> = {};
      for (const x of raw) {
        if (x?.dimCode != null) o[x.dimCode] = x.score;
      }
      return o;
    }
    if (raw.scores && typeof raw.scores === 'object' && !Array.isArray(raw.scores)) {
      return raw.scores as Record<string, number>;
    }
  }
  if (t.draft?.scores && typeof t.draft.scores === 'object') {
    return t.draft.scores as Record<string, number>;
  }
  return {};
}

/** 回显举证：已提交读 scores[].comment；草稿读 draftJson 数组项的 comment。 */
function initialCommentsFromTask(t: any): Record<string, string> {
  const out: Record<string, string> = {};
  if (t.status === 'SUBMITTED' && Array.isArray(t.scores)) {
    for (const s of t.scores) if (s?.comment) out[s.dimCode] = s.comment;
    return out;
  }
  const raw = t.draftJson;
  if (Array.isArray(raw)) {
    for (const x of raw) if (x?.dimCode != null && x.comment) out[x.dimCode] = x.comment;
  }
  return out;
}

/**
 * 360 素质评价表单。
 *
 * V1.1 §6.4：
 *  - 7 级李克特量表
 *  - 部分维度可"不了解"（V1.1 §10.1 灰名单）
 *  - 评价人不可见自己评分总分（去防参考效应）
 *  - 提交前必须二次确认
 *  - 提交时间 ≤ 20s 直接拒收（前端阻断 + 后端兜底）
 */
export function EvalFormPage() {
  const { getEvalRoleLabel } = useEvalRoleLabels();
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const [openTimestamp, setOpenTimestamp] = useState<number>(0);

  const { data, isLoading } = useQuery({
    queryKey: ['eval-task', id],
    queryFn: () => api.get(`/my/eval-tasks/${id}`),
  });

  const batchOngoing = (data as any)?.batch?.phase === 'ONGOING';

  useEffect(() => {
    if (data && openTimestamp === 0) {
      form.setFieldsValue({
        scores: initialScoresFromTask(data as any),
        comments: initialCommentsFromTask(data as any),
      });
      setOpenTimestamp(Date.now());
    }
  }, [data, form, openTimestamp]);

  const saveDraft = useMutation({
    mutationFn: (vals: any) =>
      api.put(`/my/eval-tasks/${id}/draft`, {
        scores: scoresObjectToArray(vals?.scores, vals?.comments),
      }),
    onSuccess: (_, variables, context: any) => {
      if (!context?.silent) {
        message.success('草稿已保存');
      }
    },
  });

  const currentScores = (Form.useWatch('scores', form) || {}) as Record<string, number>;
  const currentComments = (Form.useWatch('comments', form) || {}) as Record<string, string>;

  // 自动保存草稿（已提交/锁定不再写草稿，避免与提交竞态把状态打回 DRAFTED）
  useEffect(() => {
    if (!batchOngoing || !data) return;
    const status = (data as any)?.status;
    if (status === 'SUBMITTED' || status === 'LOCKED') return;
    const hasScores = Object.values(currentScores).some((v) => v !== undefined && v !== null);
    if (!hasScores) return;

    const timer = setTimeout(() => {
      saveDraft.mutate(
        { scores: currentScores, comments: currentComments },
        { context: { silent: true } } as any,
      );
    }, 1500);
    return () => clearTimeout(timer);
  }, [currentScores, currentComments, batchOngoing, data]);

  const submit = useMutation({
    mutationFn: (vals: any) =>
      api.post(`/my/eval-tasks/${id}/submit`, {
        scores: scoresObjectToArray(vals?.scores, vals?.comments),
        submitDuration: Math.floor((Date.now() - openTimestamp) / 1000),
      }),
    onSuccess: () => {
      message.success('提交成功');
      qc.invalidateQueries({ queryKey: ['my-tasks'] });
      navigate('/my/reviews');
    },
  });

  const clearAnswers = useMutation({
    mutationFn: () => api.post(`/my/eval-tasks/${id}/clear-answers`),
    onSuccess: () => {
      message.success('已清空作答，可重新打分');
      qc.invalidateQueries({ queryKey: ['eval-task', id] });
      qc.invalidateQueries({ queryKey: ['my-tasks'] });
      form.setFieldsValue({ scores: {}, comments: {} });
    },
  });

  const onSubmit = () => {
    const task = data as any;
    if (!task?.template?.dimensions) return;
    const dimsList = task.template.dimensions as DimItem[];
    const scores = (form.getFieldValue('scores') ?? {}) as Record<string, number | undefined>;
    const comments = (form.getFieldValue('comments') ?? {}) as Record<string, string | undefined>;
    if (!allQualityDimsAnswered(dimsList, scores)) {
      message.warning('请完成所有作答再提交');
      return;
    }
    const missingEvidence = dimsList.find(
      (d) =>
        isExtremeScore(scores[d.code]) &&
        commentTextLen(comments[d.code]) < EXTREME_COMMENT_MIN_LEN,
    );
    if (missingEvidence) {
      message.warning(
        `维度「${missingEvidence.name}」打了极端分，请举具体事例（至少 ${EXTREME_COMMENT_MIN_LEN} 字）`,
      );
      return;
    }
    form
      .validateFields()
      .then((vals) => {
        const duration = Math.floor((Date.now() - openTimestamp) / 1000);
        if (duration <= 20) {
          message.warning(`用时 ${duration} 秒，请认真作答`);
          return;
        }
        Modal.confirm({
          title: task.status === 'SUBMITTED' ? '确认重新提交？' : '确认提交？',
          content:
            task.status === 'SUBMITTED'
              ? '将覆盖你已提交的分数；批次截止后不可再改。'
              : '提交后在批次截止前仍可重新修改；批次一旦截止即锁定。',
          onOk: () => submit.mutate(vals),
        });
      })
      .catch(() => {
        message.warning('请完成所有作答再提交');
      });
  };

  if (isLoading) return null;
  const t = data as any;
  if (!t) return null;

  const dims = t.template.dimensions as DimItem[];

  return (
    <div>
      <Card>
        <EvalTaskStickyHeader
          pageTitle="素质评价"
          evaluatee={t.evaluatee}
          roleTag={
            <Tag color="blue" style={{ marginLeft: 12 }}>
              {getEvalRoleLabel(t.template?.pool, t.roleInEval)}
            </Tag>
          }
          batchName={t.batch?.displayName ?? t.batch?.name}
          batchDeadlineLabel={
            t.batch?.endAt ? new Date(t.batch.endAt).toLocaleDateString('zh-CN') : '—'
          }
          statusArea={
            <>
              <Tag color={taskStatusColor(t.status)}>{taskStatusLabel(t.status)}</Tag>
              {t.status === 'SUBMITTED' && t.submitAt && (
                <span style={{ marginLeft: 8, color: '#666', fontSize: 13 }}>
                  提交于 {new Date(t.submitAt).toLocaleString('zh-CN')}
                </span>
              )}
            </>
          }
          rulesSectionHeading="素质评价规则"
          rulesPopoverHeading="素质评价规则（全文）"
          rules={QUALITY_EVAL_RULES}
        />

        <Form form={form} layout="vertical" disabled={!batchOngoing} style={{ marginTop: 16 }}>
          {dims.map((d, idx) => {
            const picked = currentScores[d.code];
            const pickedAnchor =
              picked !== undefined ? anchorForDiscreteScore(d.anchors, picked) : undefined;
            const anchorsList = d.anchors?.length ? d.anchors : null;

            return (
              <Card key={d.code} size="small" style={{ marginBottom: 16 }}>
                <h4 style={{ marginBottom: 4 }}>{idx + 1}. {d.name}</h4>
                <p style={{ color: '#666', fontSize: 13, marginBottom: 12 }}>{d.desc}</p>

                {anchorsList ? (
                  <div style={{ background: '#fafafa', borderRadius: 6, padding: '10px 12px', marginBottom: 12 }}>
                    <Typography.Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                      行为锚点（对照实际表现）
                    </Typography.Text>
                    {anchorsList.map((a) => (
                      <div key={a.range} style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 12, lineHeight: 1.6 }}>
                        <Tag color={LEVEL_COLOR[a.level] || 'default'} style={{ flexShrink: 0, height: 22, marginTop: 1 }}>
                          {a.range} · {a.level}
                        </Tag>
                        <span style={{ color: '#444' }}>{a.desc}</span>
                      </div>
                    ))}
                  </div>
                ) : null}

                <Form.Item
                  name={['scores', d.code]}
                  rules={batchOngoing ? [{ required: true, message: '请评分或选不了解' }] : []}
                  style={{ marginBottom: 0 }}
                >
                  <Radio.Group buttonStyle="solid">
                    {[1, 2, 3, 4, 5, 6, 7].map((s) => {
                      const a = anchorForDiscreteScore(d.anchors, s);
                      const title = a ? `[${a.range}] ${a.level}：${a.desc}` : undefined;
                      return (
                        <Tooltip key={s} title={title}>
                          <Radio.Button value={s}>{s}</Radio.Button>
                        </Tooltip>
                      );
                    })}
                    {d.allowDontKnow && <Radio.Button value={0}>不了解</Radio.Button>}
                  </Radio.Group>
                </Form.Item>

                {pickedAnchor ? (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                    已选 <b>{picked} 分</b> ·{' '}
                    <Tag color={LEVEL_COLOR[pickedAnchor.level]} style={{ margin: 0 }}>
                      {pickedAnchor.level}
                    </Tag>
                  </div>
                ) : null}

                {isExtremeScore(picked) ? (
                  <Form.Item
                    name={['comments', d.code]}
                    style={{ marginTop: 12, marginBottom: 0 }}
                    rules={
                      batchOngoing
                        ? [
                            {
                              validator: (_, value) =>
                                commentTextLen(value) >= EXTREME_COMMENT_MIN_LEN
                                  ? Promise.resolve()
                                  : Promise.reject(
                                      new Error(
                                        `请举具体事例（至少 ${EXTREME_COMMENT_MIN_LEN} 字）`,
                                      ),
                                    ),
                            },
                          ]
                        : []
                    }
                  >
                    <Input.TextArea
                      rows={2}
                      maxLength={500}
                      showCount
                      placeholder={extremeCommentPlaceholder(Number(picked), d.name)}
                    />
                  </Form.Item>
                ) : null}
              </Card>
            );
          })}
        </Form>

        {batchOngoing && (
          <Space>
            <Button onClick={() => saveDraft.mutate(form.getFieldsValue())}>
              保存草稿
            </Button>
            <Button type="primary" onClick={onSubmit} loading={submit.isPending}>
              {t.status === 'SUBMITTED' ? '重新提交' : '提交'}
            </Button>
          </Space>
        )}

        {batchOngoing && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <Button
              danger
              type="default"
              loading={clearAnswers.isPending}
              onClick={() => {
                Modal.confirm({
                  title: '清空我的作答？',
                  content: '将删除本任务下已填的分数与草稿，任务回到「待完成」状态。批次进行中时可随时重新打分。',
                  okText: '确认清空',
                  okButtonProps: { danger: true },
                  onOk: () => clearAnswers.mutate(),
                });
              }}
            >
              清空我的作答
            </Button>
            <span style={{ marginLeft: 12, color: '#999', fontSize: 13 }}>自测/误填时用；与管理员解锁无关</span>
          </div>
        )}
      </Card>
    </div>
  );
}
